import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
  Container,
} from "@mui/material";
import ButtonComp from "./Button";
import OtpInput from "react-otp-input";
import { string } from "../constants/string";
import { useClaimantFormStore } from "../store/useClaimantFormStore";

type OtpPopupProps = {
  open: boolean;
  onClose: () => void;
  onVerify?: (otp: string) => void;
  getOtpValue: (otp: string) => void;
  otpValue: any;
  onResendOtp?: () => void;
  cellPhoneNumber?: string;
};

const OtpPopup: React.FC<OtpPopupProps> = ({
  open,
  onClose,
  onVerify,
  getOtpValue,
  otpValue,
  onResendOtp,
  cellPhoneNumber,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes in seconds

  // Restart timer when popup opens
  useEffect(() => {
    if (open) {
      setTimeLeft(120);
    }
  }, [open]);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Format time MM:SS
  const formatTime = (seconds: number) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleResendOtp = () => {
    if (onResendOtp) {
      onResendOtp();
    }
    setTimeLeft(120); // restart timer
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      aria-labelledby="otp-dialog-title"
      aria-describedby="otp-dialog-description"
      slotProps={{
        paper: {
          sx: {
            height: 500,
            borderRadius: 4,
            overflow: "hidden",
          },
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: "primary.main",
          height: 120,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Optional: Add a logo/icon */}
      </Box>

      <DialogContent
        sx={{
          p: isMobile ? 3 : 4,
          backgroundColor: theme.palette.background.default,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "340px" }}>
          <Typography variant="h2" mb={1} textAlign="center">
            {string.otpPopup.heading}
          </Typography>
          <Typography variant="body2" mb={4} textAlign="center">
            Please Enter The 6 Digits Code sent to {cellPhoneNumber}
          </Typography>
        </Box>

        <OtpInput
          value={otpValue}
          onChange={getOtpValue}
          numInputs={6}
          renderSeparator={<span></span>}
          renderInput={(props) => <input {...props} />}
          inputStyle={{
            width: "45px",
            height: "40.5px",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.background.paper}`,
            backgroundColor: theme.palette.background.paper,
            textAlign: "center",
            outline: "none",
          }}
          containerStyle={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginBottom: "30px",
          }}
          shouldAutoFocus
        />

        {timeLeft > 0 ? (
          <Typography variant="body2" textAlign="center" mb={2}>
            {string.otpPopup.infoOtp}{" "}
            <Box component="span" fontWeight="bold">
              {formatTime(timeLeft)}
            </Box>
          </Typography>
        ) : (
          <Typography
            variant="body2"
            textAlign="center"
            mb={2}
            sx={{ color: "primary.main", cursor: "pointer" }}
            onClick={handleResendOtp}
          >
            {string.otpPopup.resendOtp}
          </Typography>
        )}

        <ButtonComp
          variant="contained"
          height={50}
          width={250}
          onClick={onVerify}
        >
          {string.otpPopup.verify}
        </ButtonComp>
      </DialogContent>
    </Dialog>
  );
};

export default OtpPopup;
